import {gql} from '@apollo/client'
import {isRight} from 'fp-ts/lib/Either'
import React, {useEffect, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import styled from 'styled-components'
import {
  CardApprovalConfiguration,
  CardApprovalConfigurationCodec
} from '../../../common/types'
import {Form} from '../../../components/form'
import {DropdownSelect} from '../../../components/library/dropdown/select'
import {Icon} from '../../../components/library/icon/icon'
import {NoResultsPermissions} from '../../../components/library/list'
import {Switch} from '../../../components/library/toggles/switch'
import {Loader} from '../../../components/loader'
import {
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {EnvironmentNode} from '../../../environment'
import {AlertDialog} from '../../../hooks/use-confirm'
import {useStateChanged} from '../../../hooks/use-state-changed'
import {useAnalytics} from '../../../lib/analytics/analytics'
import {useToast} from '../../../lib/toast'
import {omit} from '../../../lib/updaters'
import {IconHeading, Section, SectionContent, SectionInfo} from '../components'
import {
  useCardApprovalConfigurationQuery,
  useSaveApprovalConfigurationMutation
} from './generated'

const Wrapper = styled.div`
  padding: 20px;
  background-color: white;
  border-radius: ${p => p.theme.borderRadius};
  > * {
    margin-bottom: 20px;
  }
`

export const CardApprovals = ({
  environment
}: {
  environment: EnvironmentNode
}) => {
  const [ready, setReady] = useState(false)
  const toast = useToast()
  const [localObj, setLocalObj, changed, resetState] = useStateChanged<
    Partial<CardApprovalConfiguration>
  >({})
  const canView = environment.hasPermission(
    PermissionResource.Environment,
    PermissionAction.View
  )
  const canEdit = environment.hasPermission(
    PermissionResource.Environment,
    PermissionAction.Admin
  )

  const analytics = useAnalytics()

  const {data: query, loading} = useCardApprovalConfigurationQuery({
    variables: {
      environmentId: environment.id
    },
    onCompleted: data => {
      if (data.approvalConfiguration?.configuration) {
        resetState(data.approvalConfiguration.configuration)
      }
      setReady(true)
    }
  })

  const [doMutation, {loading: saving, error}] =
    useSaveApprovalConfigurationMutation({
      onCompleted: data => {
        if (data.saveApprovalConfiguration?.configuration) {
          resetState(data.saveApprovalConfiguration?.configuration)
          analytics.track({
            event: 'Edited configuration',
            properties: {
              org_id: environment.organisationId,
              environment_id: environment.id,
              category: 'configuration>card-templates>approvals'
            }
          })
        }
        toast('Changes saved')
      }
    })

  const validate = (
    val: Partial<CardApprovalConfiguration>
  ): val is CardApprovalConfiguration => {
    const decode = CardApprovalConfigurationCodec.decode(val)
    return (
      isRight(decode) &&
      (!decode.right.enabled || decode.right.requiredGroups.length > 0)
    )
  }

  const onSave = async () => {
    if (validate(localObj)) {
      await doMutation({
        variables: {
          environmentId: environment.id,
          configuration: omit(localObj, '__typename')
        }
      })
    }
  }

  const onReset = () => {
    if (query?.approvalConfiguration?.configuration) {
      resetState(query?.approvalConfiguration.configuration)
    }
  }

  useEffect(() => {
    analytics.track({
      event: 'Viewed card templates approvals configuration',
      properties: {
        org_id: environment.organisationId,
        environment_id: environment.id,
        category: 'configuration>card-templates>approvals'
      }
    })
  }, [analytics, environment])

  return (
    <Section>
      <Helmet>
        <title>Approvals</title>
      </Helmet>
      <header>
        <IconHeading>
          <Icon name="check" /> Approvals
        </IconHeading>
      </header>

      <SectionInfo>
        <p>
          Configure the approval process for Action Flows and card templates
        </p>
      </SectionInfo>

      <SectionContent>
        {!ready ? (
          <Loader />
        ) : canView ? (
          <Form
            onSubmit={async () => {
              if (validate(localObj)) {
                await doMutation({
                  variables: {
                    environmentId: environment.id,
                    configuration: omit(localObj, '__typename')
                  }
                })
              }
            }}
            onCancel={changed ? onReset : undefined}
            submitText="Update"
            cancelText="Discard changes"
            error={error}
            isValid={changed && !loading && validate(localObj)}
            loading={loading || saving}
          >
            <Wrapper>
              <Switch
                disabled={!canEdit}
                label="Require approvals"
                isForm={true}
                labelPosition="left"
                checked={!!localObj.enabled}
                onChange={enabled =>
                  setLocalObj(c => ({
                    ...c,
                    enabled
                  }))
                }
              />
              <DropdownSelect
                label="Require approval from these groups"
                isMultiSelect={true}
                options={
                  query?.groups?.nodes.map(g => ({
                    text: g.name,
                    value: g.id
                  })) ?? []
                }
                value={localObj.requiredGroups ?? []}
                onChange={requiredGroups => {
                  if (canEdit) {
                    setLocalObj(c => ({
                      ...c,
                      requiredGroups: requiredGroups.map(g => String(g))
                    }))
                  }
                }}
              />
              {/* <Select
                disabled={!canEdit}
                label="Number of approvals required"
                options={[1, 2, 3, 4]}
                value={localObj.requiredCount || 1}
                onChange={count =>
                  setLocalObj(c => ({
                    ...c,
                    requiredCount: count ?? 1
                  }))
                }
              /> */}
            </Wrapper>
          </Form>
        ) : (
          <div style={{margin: '50px 0px'}}>
            <NoResultsPermissions message="Sorry, you don't have permission to view these settings" />
          </div>
        )}
      </SectionContent>
      <AlertDialog
        isBlocking={changed}
        onSave={validate(localObj) ? onSave : undefined}
      >
        Would you like to save configuration before you leave?
      </AlertDialog>
    </Section>
  )
}

gql`
  query CardApprovalConfiguration($environmentId: String!) {
    approvalConfiguration(environmentId: $environmentId) {
      configuration {
        enabled
        requiredCount
        requiredGroups
      }
    }
    groups: environmentGroups(environmentId: $environmentId) {
      nodes {
        id
        name
      }
    }
  }
  mutation SaveApprovalConfiguration(
    $environmentId: String!
    $configuration: ApprovalConfigurationInput!
  ) {
    saveApprovalConfiguration(
      environmentId: $environmentId
      configuration: $configuration
    ) {
      configuration {
        enabled
        requiredCount
        requiredGroups
      }
    }
  }
`
